import { useEffect, useState, type FormEvent } from 'react';
import { PROJECT_STATUS, PROJECT_STATUS_LABELS, type ProjectInput, type ProjectStatus } from '@stromo/shared';
import { useContactsLookup, useUsersLookup, useTemplatesLookup } from '../lib/lookups';

interface ProjectFormProps {
  initial?: Partial<ProjectInput>;
  /** Vorlage-Feld nur beim Anlegen anzeigen */
  isCreate?: boolean;
  onSubmit: (input: ProjectInput) => void;
  onCancel: () => void;
  submitting?: boolean;
  errorMessage?: string | null;
}

const EMPTY: ProjectInput = {
  order_number: '',
  title: '',
  client_id: null,
  architect_id: null,
  project_manager_id: null,
  site_manager_id: null,
  start_date: null,
  end_date: null,
  status: 'open',
  description: null,
  notes: null,
  acceptance_planned: null,
  acceptance_done: false,
  is_archived: false,
  template_id: null,
};

export function ProjectForm({
  initial,
  isCreate = false,
  onSubmit,
  onCancel,
  submitting,
  errorMessage,
}: ProjectFormProps) {
  const [form, setForm] = useState<ProjectInput>({ ...EMPTY, ...initial });

  useEffect(() => {
    setForm({ ...EMPTY, ...initial });
  }, [initial]);

  const users = useUsersLookup();
  const clients = useContactsLookup('client');
  const architects = useContactsLookup('architect');
  const templates = useTemplatesLookup();

  const handle = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(form);
  };

  const set = <K extends keyof ProjectInput>(key: K, value: ProjectInput[K]) =>
    setForm((s) => ({ ...s, [key]: value }));

  const optionalNumber = (v: string): number | null => (v ? Number(v) : null);

  return (
    <form onSubmit={handle} noValidate>
      {errorMessage && <div className="login-error">{errorMessage}</div>}

      <div className="form-grid">
        <div className="form-group">
          <label className="form-label" htmlFor="order_number">
            Auftragsnummer *
          </label>
          <input
            id="order_number"
            required
            value={form.order_number}
            placeholder="z.B. 2026101"
            onChange={(e) => set('order_number', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="title">
            Projektbezeichnung *
          </label>
          <input
            id="title"
            required
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="client_id">
            Bauherr
          </label>
          <select
            id="client_id"
            value={form.client_id ?? ''}
            onChange={(e) => set('client_id', optionalNumber(e.target.value))}
          >
            <option value="">— wählen —</option>
            {clients.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="architect_id">
            Architekt / GU
          </label>
          <select
            id="architect_id"
            value={form.architect_id ?? ''}
            onChange={(e) => set('architect_id', optionalNumber(e.target.value))}
          >
            <option value="">— wählen —</option>
            {architects.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="project_manager_id">
            Projektleiter
          </label>
          <select
            id="project_manager_id"
            value={form.project_manager_id ?? ''}
            onChange={(e) => set('project_manager_id', optionalNumber(e.target.value))}
          >
            <option value="">— wählen —</option>
            {users.data
              ?.filter((u) => u.role === 'project_manager' || u.role === 'admin')
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name} ({u.abbreviation})
                </option>
              ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="site_manager_id">
            Bauleiter
          </label>
          <select
            id="site_manager_id"
            value={form.site_manager_id ?? ''}
            onChange={(e) => set('site_manager_id', optionalNumber(e.target.value))}
          >
            <option value="">— wählen —</option>
            {users.data
              ?.filter((u) => u.role === 'site_manager' || u.role === 'admin')
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name} ({u.abbreviation})
                </option>
              ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="start_date">
            Auftragsstart
          </label>
          <input
            id="start_date"
            type="date"
            value={form.start_date ?? ''}
            onChange={(e) => set('start_date', e.target.value || null)}
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="end_date">
            Auftragsende
          </label>
          <input
            id="end_date"
            type="date"
            value={form.end_date ?? ''}
            onChange={(e) => set('end_date', e.target.value || null)}
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="status">
            Status
          </label>
          <select
            id="status"
            value={form.status ?? 'open'}
            data-status={form.status ?? 'open'}
            onChange={(e) => set('status', e.target.value as ProjectStatus)}
          >
            {PROJECT_STATUS.map((s) => (
              <option key={s} value={s}>
                {PROJECT_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="acceptance_planned">
            Abnahme geplant
          </label>
          <input
            id="acceptance_planned"
            type="date"
            value={form.acceptance_planned ?? ''}
            onChange={(e) => set('acceptance_planned', e.target.value || null)}
          />
        </div>

        {isCreate && (
          <div className="form-group full">
            <label className="form-label" htmlFor="template_id">
              Vorlage (Detailpositionen werden kopiert)
            </label>
            <select
              id="template_id"
              value={form.template_id ?? ''}
              onChange={(e) => set('template_id', optionalNumber(e.target.value))}
            >
              <option value="">— keine Vorlage —</option>
              {templates.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.description ? ` — ${t.description}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group full">
          <label className="form-label" htmlFor="description">
            Beschreibung
          </label>
          <textarea
            id="description"
            value={form.description ?? ''}
            onChange={(e) => set('description', e.target.value || null)}
            rows={5}
            placeholder="Stammdaten-Infos wie Türcodes, Telefonnummern von Key-Personen, Schlüssel-Standorte usw."
          />
        </div>

        <div className="form-group full">
          <label className="form-label" htmlFor="notes">
            Bemerkungen / Nächste Schritte
          </label>
          <textarea
            id="notes"
            value={form.notes ?? ''}
            onChange={(e) => set('notes', e.target.value || null)}
          />
        </div>

        <div className="form-group full" style={{ flexDirection: 'row', gap: 18, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-text-strong)', textTransform: 'none', letterSpacing: 0 }}>
            <input
              type="checkbox"
              checked={form.acceptance_done ?? false}
              onChange={(e) => set('acceptance_done', e.target.checked)}
              style={{ width: 16, height: 16 }}
            />
            Abnahme erfolgt
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-text-strong)', textTransform: 'none', letterSpacing: 0 }}>
            <input
              type="checkbox"
              checked={form.is_archived ?? false}
              onChange={(e) => set('is_archived', e.target.checked)}
              style={{ width: 16, height: 16 }}
            />
            Archiviert
          </label>
        </div>
      </div>

      <div
        className="modal-footer"
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 10,
          marginTop: 22,
          paddingTop: 16,
          borderTop: '1px solid var(--color-border-soft)',
        }}
      >
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={submitting}>
          Abbrechen
        </button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Speichere …' : isCreate ? 'Projekt anlegen' : 'Änderungen speichern'}
        </button>
      </div>
    </form>
  );
}
